import { Button, ButtonRouter } from "~/components/ui/button";
import { Modal } from "~/components/ui/modal/modal";
import { BILLING_PATH } from "~/lib/shared/return-path";
import {
  type AccountDeletionRefusal,
  USER_DELETION_GRACE_PERIOD_DAYS,
} from "~/lib/shared/user-deletion";

interface DeactivateAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  /** Why the last request was refused, shown in place of the confirmation. */
  refusal?: AccountDeletionRefusal | null;
}

export const DeactivateAccountModal = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  refusal,
}: DeactivateAccountModalProps) => {
  return (
    <Modal.Root open={isOpen} onOpenChange={onClose}>
      <Modal.Panel size="lg" className="max-w-2xl">
        <Modal.Title className="sr-only">Deactivate Account</Modal.Title>

        <div className="flex flex-col items-start space-y-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-brand-50">
            <span className="text-4xl text-orange-200">?</span>
          </div>

          {refusal ? (
            <>
              <h2 className="text-2xl font-medium text-gray-900">
                You can’t deactivate your account yet
              </h2>

              <p role="alert" className="text-gray-600">
                {refusal.message}
              </p>

              <div className="flex w-full justify-between">
                <Button onClick={onClose} className="text-base">
                  Close
                </Button>
                {refusal.reason === "billing_subscription" && (
                  <ButtonRouter
                    to={BILLING_PATH}
                    variant="primary"
                    className="px-4 text-base"
                  >
                    Go to Billing
                  </ButtonRouter>
                )}
              </div>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-medium text-gray-900">
                Confirm Account Deactivation
              </h2>

              <div className="space-y-1 text-gray-600">
                <p>
                  {`Are you sure you want to deactivate your account? In ${USER_DELETION_GRACE_PERIOD_DAYS} days, your account and all associated data will be permanently deleted. Until then, you can cancel the deletion from your profile settings.`}
                </p>
              </div>

              <div className="flex w-full justify-between">
                <Button onClick={onClose} className="text-base">
                  Cancel
                </Button>
                <Button
                  onClick={onConfirm}
                  disabled={isLoading}
                  loading={isLoading}
                  variant="destructive"
                  className="bg-red-600 px-4 text-white hover:bg-red-700"
                >
                  Deactivate Account
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal.Panel>
    </Modal.Root>
  );
};
